<template>
    <nav>
        <!-- ── MAIN MODULES: plain-list style (top) ── -->
        <div
            v-if="
                groupedModules['MAIN MODULES'] &&
                groupedModules['MAIN MODULES'].length > 0
            "
            class="catalyst-list-section"
        >
            <p class="catalyst-list-label">Main</p>
            <template
                v-for="module in groupedModules['MAIN MODULES']"
                :key="module.id"
            >
                <div
                    v-if="module.link !== '-' && module.submodule.length === 0"
                    class="catalyst-list-item"
                    :class="{
                        'catalyst-list-item--active': isModuleActive(module),
                    }"
                    @click="setCurrentModule(module.id)"
                >
                    <router-link :to="module.link" class="catalyst-list-link">
                        <span>{{ module.name }}</span>
                        <v-icon
                            v-if="module.link === '/ask-ai'"
                            :icon="mdiStarFourPoints"
                            size="16"
                            class="ml-1 ai-gradient-color"
                        />
                    </router-link>
                </div>
            </template>
        </div>

        <!-- ── Modules WITH submodules: hover popup menu ── -->
        <div class="catalyst-menu-section">
            <template
                v-for="(moduleGroup, groupName) in groupedModules"
                :key="groupName"
            >
                <template v-if="groupName !== 'MAIN MODULES'">
                    <v-menu
                        v-for="module in moduleGroup"
                        :key="module.id"
                        :model-value="activeMenuId === module.id"
                        @update:model-value="(v) => onMenuUpdate(module.id, v)"
                        location="end"
                        :offset="6"
                        :open-on-hover="false"
                        :open-on-click="true"
                        :close-on-content-click="false"
                        content-class="submodule-popup"
                    >
                        <template #activator="{ props }">
                            <div
                                v-bind="props"
                                class="catalyst-nav-item"
                                :class="{
                                    'catalyst-nav-item--active':
                                        isModuleActive(module),
                                }"
                            >
                                <v-icon
                                    :icon="getModuleIcon(module.icon)"
                                    size="18"
                                    class="catalyst-nav-icon"
                                />
                                <span class="catalyst-nav-label">{{
                                    module.name
                                }}</span>
                                <v-icon
                                    :icon="mdiChevronRight"
                                    size="14"
                                    class="chevron-right-icon"
                                />
                            </div>
                        </template>

                        <!-- Popup menu content -->
                        <div class="submodule-popup-inner">
                            <p class="submodule-popup-label">
                                {{ module.name }}
                            </p>
                            <div
                                v-for="child in module.submodule"
                                :key="child.id"
                                class="submodule-popup-item"
                                :class="{
                                    'submodule-popup-item--active':
                                        child.id === current_sub_module_id ||
                                        $route.path === child.link,
                                }"
                                @click="
                                    setCurrentModule(module.id, child.id);
                                    activeMenuId = null;
                                "
                            >
                                <router-link
                                    :to="child.link"
                                    class="submodule-popup-link"
                                >
                                    {{ child.name }}
                                </router-link>
                            </div>
                        </div>
                    </v-menu>
                </template>
            </template>
        </div>
    </nav>
</template>

<script>
import {
    mdiViewDashboardOutline,
    mdiFileDocumentMultipleOutline,
    mdiAccountOutline,
    mdiAccountGroupOutline,
    mdiChartAreaspline,
    mdiCircleSmall,
    mdiAccountDetailsOutline,
    mdiSecurity,
    mdiToyBrickOutline,
    mdiWarehouse,
    mdiFileCabinet,
    mdiFileSign,
    mdiTruckFastOutline,
    mdiCogOutline,
    mdiPackageVariant,
    mdiChartBarStacked,
    mdiChevronRight,
    mdiStarFourPoints,
    mdiCurrencyUsd,
    mdiHandshakeOutline,
    mdiBeakerOutline,
    mdiCubeOutline,
    mdiFileDocumentEditOutline,
    mdiChartDonut,
} from "@mdi/js";
import { mapState } from "vuex";
import processErrors from "@/js/ProcessErrors";

export default {
    props: {
        project_type: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            current_module_id: null,
            current_sub_module_id: null,
            activeMenuId: null,
            isTouchDevice: false,
            mdiChevronRight,
            mdiStarFourPoints,
        };
    },
    mounted() {
        this.isTouchDevice =
            "ontouchstart" in window || navigator.maxTouchPoints > 0;
    },
    async created() {
        await this.reloadAllModules();
    },
    computed: {
        // Modules come from the permissions store (single source of truth).
        // The store dedupes concurrent loads triggered by router/sidebar/etc.
        ...mapState("permissions", {
            AVAILABLE_MODULES_DECODED: (state) => state.modules || [],
        }),
        groupedModules() {
            // const get_current_project_type = JSON.parse(
            //     localStorage.getItem("project"),
            // ).type.toLowerCase();

            const groups = {};
            this.AVAILABLE_MODULES_DECODED.forEach((module) => {
                if (module.submodule && module.submodule.length > 0) {
                    // 如果有子模块，那么这个模块的 name 就是分组名
                    const groupName = module.name.toUpperCase();
                    if (!groups[groupName]) {
                        groups[groupName] = [];
                    }
                    // 将这个模块本身作为分组的代表
                    groups[groupName].push(module);
                } else {
                    // 如果没有子模块，放到 "MAIN MODULES" 分组
                    const groupName = "MAIN MODULES";
                    if (!groups[groupName]) {
                        groups[groupName] = [];
                    }

                    if (module.link != "/UserProfile") {
                        groups[groupName].push(module);
                    }
                }
            });

            return groups;
        },
    },
    methods: {
        onMenuUpdate(moduleId, value) {
            // Click activator opens this menu (replacing any other open one);
            // outside click / submodule click closes it.
            if (value) {
                this.activeMenuId = moduleId;
            } else if (this.activeMenuId === moduleId) {
                this.activeMenuId = null;
            }
        },
        async reloadAllModules() {
            // Delegate to the permissions store. It dedupes concurrent calls
            // and persists to localStorage as a hot-reload bootstrap cache.
            try {
                await this.$store.dispatch(
                    "permissions/ensureLoaded",
                    this.project_type,
                );
            } catch (error) {
                processErrors();
            }
        },
        setCurrentModule(id, sub_module_id = 0) {
            this.current_module_id = id;
            if (sub_module_id > 0) {
                localStorage.setItem("sub_module_id", sub_module_id);
                this.current_sub_module_id = sub_module_id;
            } else {
                localStorage.removeItem("sub_module_id");
                this.current_sub_module_id = null;
            }
        },
        isModuleActive(module) {
            // Check if this module or any of its submodules is active
            if (module.submodule.length === 0) {
                return this.$route.path === module.link;
            } else {
                return module.submodule.some(
                    (sub) => this.$route.path === sub.link,
                );
            }
        },
        getModuleIcon(val) {
            switch (val) {
                case "dashboard_icon":
                    return mdiViewDashboardOutline;
                case "kpi_icon":
                    return mdiChartAreaspline;
                case "project_icon":
                    return mdiFileDocumentMultipleOutline;
                case "customer_icon":
                    return mdiAccountDetailsOutline;
                case "hr_icon":
                    return mdiAccountGroupOutline;
                case "user_profile_icon":
                    return mdiAccountOutline;
                case "access_icon":
                    return mdiSecurity;
                case "mould_icon":
                    return mdiToyBrickOutline;
                case "tunnel_warehouse_icon":
                case "bridge_warehouse_icon":
                case "warehouse_icon":
                    return mdiWarehouse;
                case "pricing_icon":
                    return mdiCurrencyUsd;
                case "file_icon":
                    return mdiFileCabinet;
                case "contract_icon":
                    return mdiFileSign;
                case "bridge_delivery_icon":
                case "tunnel_delivery_icon":
                case "delivery_icon":
                    return mdiTruckFastOutline;
                case "tunnel_setting_icon":
                case "setting_icon":
                    return mdiCogOutline;
                case "production_icon":
                    return mdiPackageVariant;
                case "chart_icon":
                    return mdiChartBarStacked;
                case "manpower_icon":
                    return mdiHandshakeOutline;
                case "mdiBeakerOutline":
                    return mdiBeakerOutline;
                case "material_usages_icon":
                    return mdiCubeOutline;
                case "progress_claims_icon":
                    return mdiFileDocumentEditOutline;
                case "mdiChartDonut":
                    return mdiChartDonut;
                default:
                    return mdiCircleSmall;
            }
        },
    },
};
</script>

<style scoped>
/* ── MAIN MODULES: plain-list section (top) ── */
.catalyst-list-section {
    margin-bottom: 8px;
}

.catalyst-list-label {
    padding: 0 8px 6px;
    margin: 0;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--sidebar-text-muted);
}

.catalyst-list-item {
    padding: 1px 0;
}

.catalyst-list-link {
    display: flex;
    align-items: center;
    padding: 6px 8px;
    border-radius: var(--radius-md);
    font-size: 14px;
    color: var(--sidebar-text-secondary);
    text-decoration: none;
    transition: background 0.12s, color 0.12s;
}

.catalyst-list-link:hover {
    background-color: var(--border);
    color: var(--sidebar-text-primary);
    text-decoration: none;
}

.catalyst-list-item--active .catalyst-list-link {
    background-color: var(--sidebar-accent);
    color: var(--primary);
    font-weight: 500;
}

/* ── Modules with submodules: hover-menu section ── */
.catalyst-menu-section {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--sidebar-border);
}

/* Nav item trigger */
.catalyst-nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border-radius: var(--radius-md);
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: var(--card-foreground);
    transition: background 0.12s, color 0.12s;
    user-select: none;
    margin-bottom: 2px;
}

.catalyst-nav-item:hover {
    background-color: var(--border);
    color: var(--card-foreground);
}

.catalyst-nav-item--active {
    background-color: var(--accent);
    color: var(--accent-foreground);
    font-weight: 600;
}

.catalyst-nav-icon {
    flex-shrink: 0;
    color: var(--sidebar-text-muted);
}

.catalyst-nav-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.chevron-right-icon {
    flex-shrink: 0;
    color: var(--sidebar-text-muted);
}
</style>

<style>
/* ── Submodule popup menu (not scoped — Vuetify teleports outside component) ── */
.submodule-popup {
    border-radius: var(--radius) !important;
    box-shadow: 0 0 0 1px var(--border-color);
    overflow: hidden;
}

.submodule-popup-inner {
    min-width: 200px;
    background: var(--popover);
    color: var(--popover-foreground);
    padding: 6px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    border: 1px solid var(--border);
    border-radius: var(--radius-xl);
}

.submodule-popup-label {
    margin: 0;
    padding: 4px 10px 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--muted-foreground);
}

.submodule-popup-item {
    border-radius: 6px;
    overflow: hidden;
}

.submodule-popup-link {
    display: block;
    padding: 7px 10px;
    font-size: 13.5px;
    color: var(--card-foreground);
    text-decoration: none;
    border-radius: 6px;
    transition: background 0.1s, color 0.1s;
}

.submodule-popup-link:hover {
    background-color: var(--accent, #f3f4f6);
    color: var(--accent-foreground, #111827);
    text-decoration: none;
}

.submodule-popup-item--active .submodule-popup-link {
    background-color: var(--accent);
    color: var(--sidebar-primary, #3b82f6);
    font-weight: 500;
}
</style>
